export interface IDistributableColumn {
  id: string;
  size: number;
  maxSize: number | undefined;
  pinned?: boolean;
  stretchPercent?: number;
}

const shareProportionally = (
  columns: IDistributableColumn[],
  widths: Map<string, number>,
  amount: number,
) => {
  const weight = columns.reduce((sum, c) => sum + c.size, 0);
  for (const c of columns) {
    widths.set(c.id, (widths.get(c.id) ?? 0) + (amount * c.size) / weight);
  }
};

const isCapped = (c: IDistributableColumn, width: number) =>
  c.maxSize !== undefined && width >= c.maxSize;

/** Hands out `surplus` in proportion to size, water-filling around `maxSize` caps. Whatever is
 * left once every column is capped stays unassigned. */
const fillUpToCaps = (
  flexible: IDistributableColumn[],
  widths: Map<string, number>,
  initialSurplus: number,
) => {
  let surplus = initialSurplus;
  let uncapped = flexible.filter((c) => !isCapped(c, c.size));
  while (surplus > 0 && uncapped.length > 0) {
    const weight = uncapped.reduce((sum, c) => sum + c.size, 0);
    const overflowing = uncapped.filter((c) =>
      isCapped(c, (widths.get(c.id) ?? 0) + (surplus * c.size) / weight),
    );
    if (overflowing.length === 0) {
      shareProportionally(uncapped, widths, surplus);
      return;
    }
    for (const c of overflowing) {
      const cap = c.maxSize ?? 0;
      surplus -= cap - (widths.get(c.id) ?? 0);
      widths.set(c.id, cap);
    }
    uncapped = uncapped.filter((c) => !overflowing.includes(c));
  }
};

/** Whole pixels. Flooring loses up to a pixel per column; those go back one at a time, last
 * column first, to columns still under their cap — so a cap is never exceeded by rounding. */
const roundToContainer = (
  columns: IDistributableColumn[],
  flexible: IDistributableColumn[],
  widths: Map<string, number>,
  containerWidth: number,
): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const c of columns) result[c.id] = Math.floor(widths.get(c.id) ?? 0);
  let remainder =
    containerWidth - Object.values(result).reduce((sum, w) => sum + w, 0);
  for (const c of [...flexible].reverse()) {
    if (remainder <= 0) break;
    const width = result[c.id] ?? 0;
    if (c.maxSize !== undefined && width >= c.maxSize) continue;
    result[c.id] = width + 1;
    remainder -= 1;
  }
  return result;
};

/**
 * Pixel widths that make a `table-fixed` table fill `containerWidth`.
 *
 * Every column must get an explicit px width because a `table-fixed` table narrower than its
 * container spreads the surplus over ALL columns — sticky pinned ones too — while the pinned
 * columns' `left`/`right` offsets are computed from their declared sizes, so they overlap.
 *
 * - pinned: exactly `size`
 * - unpinned: `size` plus a share of the surplus proportional to `size`, capped at `maxSize`;
 *   what a capped column can't take goes to the others.
 * - never below `size`: a container narrower than the sizes scrolls rather than squeezing.
 *
 * Caps always hold. When no column can take the surplus (all capped, or all pinned) the widths
 * sum to less than the container, and the caller must size the table to that sum rather than
 * let it stretch — otherwise the browser hands the surplus to the pinned columns.
 *
 * `null` means "leave the layout to the declared sizes": the container isn't measured yet, or an
 * unpinned column is sized by percentage (`meta.stretch`), which already fills by itself.
 */
export const distributeColumnWidths = (
  columns: IDistributableColumn[],
  containerWidth: number,
): Record<string, number> | null => {
  if (containerWidth <= 0) return null;
  // A pinned column ignores its percentage (`getPinningStyles`), so only an unpinned one opts
  // the table into the percentage layout.
  if (columns.some((c) => !c.pinned && c.stretchPercent !== undefined)) {
    return null;
  }

  const widths = new Map(columns.map((c) => [c.id, c.size]));
  const flexible = columns.filter((c) => !c.pinned);
  const surplus = containerWidth - columns.reduce((sum, c) => sum + c.size, 0);

  if (flexible.length > 0 && surplus > 0) {
    fillUpToCaps(flexible, widths, surplus);
  }

  return roundToContainer(columns, flexible, widths, containerWidth);
};

/**
 * The px width to pin the table to when the distributed widths fall short of the container (no
 * column may take the spare width: all capped, or all pinned); `undefined` when they fill it. A
 * `w-full` table would otherwise hand the gap to every column, pinned ones included.
 */
export const getShortfallTableWidth = (
  widths: Record<string, number> | null,
  containerWidth: number,
): number | undefined => {
  if (!widths) return undefined;
  const assigned = Object.values(widths).reduce((sum, w) => sum + w, 0);
  return assigned < containerWidth ? assigned : undefined;
};
